import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import CreateAgent from '../agents/ConfigureAgent';
import useAnalytics from '../analytics/analytics';
import { formatDate } from '../utils/dateFormatter';
import { Separator } from '@/components/ui/separator';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Download, Globe, Monitor, MoreHorizontal, ShieldCheck, Trash2 } from 'lucide-react';
import { LeadSheet } from './LeadSheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import PageSizeSelect, { DEFAULT_PAGE_SIZE } from "@/components/ui/PageSizeSelect";
import { useTableFilters, DataTableToolbar, useTableSort, SortControl, SortHeader } from "@/components/shared/filters";
import { LEADS_FILTERS, LEADS_SORTS, LEADS_DEFAULT_SORT } from "./leadsFilters";
import SalesforceIcon from '../icons/salesforce_logo.png';
import HubspotIcon from '../icons/hubspot_logo.webp';
import AttioIcon from '../icons/attio_logo.png';
import { cn } from "@/lib/utils";
import ObjectiveProgress, { ObjectiveProgressValue } from '../objectives/ObjectiveProgress';

interface Lead {
  id: string;
  organization_id?: string;
  account_id?: string | null;
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  email_verified?: boolean | null;
  identity_verified_at?: string | null;
  job_title?: string;
  revenue?: number | null;
  integrations?: {
    salesforce?: string;
    salesforce_error?: string;
    salesforce_contact?: string;
    salesforce_contact_error?: string;
    hubspot?: string;
    hubspot_error?: string;
    attio?: string;
    attio_error?: string;
    google_ads_conversion_status?: string;
    google_ads_conversion_error?: string;
    google_ads_error?: string;
    hubspot_deals?: any[];
    salesforce_opportunities?: any[];
  };
  source?: string;
  external_id?: string;
  created_at?: string;
  updated_at?: string;
  attributes?: {
    loc?: string | null;
    org?: string | null;
    city?: string | null;
    email?: string;
    postal?: string | null;
    region?: string | null;
    country?: string | null;
    hostname?: string | null;
    last_name?: string;
    first_name?: string;
    ip_address?: string | null;
    aimdoc_parent_url?: string;
  };
}

type SelectedLead = Lead | null;

const PAGE_SIZE_STORAGE_KEY = 'contacts:pageSize';

const readStoredPageSize = (): number => {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const stored = parseInt(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || '', 10);
  return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_PAGE_SIZE;
};

const escapeCsv = (value: string) => {
  let str = String(value ?? '');
  // A cell starting with = + - @ or a tab/CR is executed as a formula by
  // Excel and Sheets; visitor-supplied names must open as text.
  if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const getResponsiveColumnClass = (columnId: string) => {
  switch (columnId) {
    case 'company':
    case 'objectives':
      return 'hidden xl:table-cell';
    case 'revenue':
    case 'source':
      return 'hidden 2xl:table-cell';
    case 'created_at':
      return 'hidden lg:table-cell';
    default:
      return '';
  }
};

const ContactActions: React.FC<{
  contactName: string;
  onDelete: () => void | Promise<void>;
}> = ({ contactName, onDelete }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  return (
    <div data-row-actions onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            aria-label={`Actions for ${contactName}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete contact
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete contact?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {contactName}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteDialogOpen(false);
                void onDelete();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LeadLists: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState<number>(readStoredPageSize);
  const [showModal, setShowModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<SelectedLead>(null);
  const [objectiveProgress, setObjectiveProgress] = useState<Record<string, ObjectiveProgressValue>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { get, post, remove } = useApi();
  const { captureEvent } = useAnalytics();

  const leadsFilters = LEADS_FILTERS;
  const {
    values: filterValues,
    setFilter,
    clearFilter,
    clearAll,
    searchValue,
    setSearch,
  } = useTableFilters(leadsFilters);
  const {
    activeField: sortField,
    activeOrder: sortOrder,
    isDefault: isDefaultSort,
    setSort,
    toggleField,
    clear: clearSort,
  } = useTableSort(LEADS_DEFAULT_SORT);

  const toggleModal = () => {
    setShowModal(!showModal);
    navigate('/contacts');
  };

  // URL is the source of truth: every filter / page / page-size change writes the
  // query string, and this effect refetches in response.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    const perPageParam = parseInt(params.get('per_page') || String(readStoredPageSize()), 10);
    const resolvedPerPage = Number.isFinite(perPageParam) && perPageParam > 0 ? perPageParam : readStoredPageSize();
    setPerPage(resolvedPerPage);
    getLeads({
      page: Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1,
      perPage: resolvedPerPage,
      search: params.get('search') || '',
      dateRange: params.get('date_range') || '',
      source: params.get('source') || '',
      sortBy: params.get('sort_by') || '',
      sortOrder: params.get('sort_order') || '',
    });
  }, [location.search]);

  const getLeads = async ({
    page = 1,
    perPage: perPageArg = readStoredPageSize(),
    search = '',
    dateRange = '',
    source = '',
    sortBy = '',
    sortOrder = '',
  }: {
    page?: number;
    perPage?: number;
    search?: string;
    dateRange?: string;
    source?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        paginated: 'true',
        page: String(page),
        per_page: String(perPageArg),
      });
      if (search) params.set('search', search);
      if (dateRange) params.set('date_range', dateRange);
      if (source) params.set('source', source);
      if (sortBy) params.set('sort_by', sortBy);
      if (sortOrder) params.set('sort_order', sortOrder);

      const data = await get(`/leads/?${params.toString()}`);

      setLeads(data.data.items);
      void fetchObjectiveProgress(data.data.items);
      setPage(data.data.page);
      setTotalPages(data.data.pages);
      captureEvent('viewed_leads', {});
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
    setLoading(false);
  };

  const fetchObjectiveProgress = async (items: Lead[]) => {
    if (!items.length) {
      setObjectiveProgress({});
      return;
    }
    try {
      const response = await post('/leads/objectives/summary', {
        lead_ids: items.map((lead) => lead.id),
      });
      setObjectiveProgress(response.data?.items || {});
    } catch (error) {
      console.error('Error fetching contact objective progress:', error);
      setObjectiveProgress({});
    }
  };

  const goToPage = (nextPage: number) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(nextPage));
    navigate(`?${params.toString()}`);
  };

  const handlePerPageChange = (value: number) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(value));
    }
    const params = new URLSearchParams(location.search);
    params.set('per_page', String(value));
    params.set('page', '1');
    navigate(`?${params.toString()}`);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await remove(`/leads/${id}`);
      const params = new URLSearchParams(location.search);
      const curPage = parseInt(params.get('page') || '1', 10);
      const targetPage = leads.length === 1 && curPage > 1 ? curPage - 1 : curPage;
      if (targetPage !== curPage) {
        goToPage(targetPage);
      } else {
        await getLeads({
          page: targetPage,
          perPage,
          search: params.get('search') || '',
          dateRange: params.get('date_range') || '',
          source: params.get('source') || '',
          sortBy: params.get('sort_by') || '',
          sortOrder: params.get('sort_order') || '',
        });
      }
      toast.success('Contact deleted successfully', toastConfig);
      return response.data;
    } catch (error) {
      console.error('Error deleting Lead:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);

      // Pull the full (filtered) contact set rather than just the current page.
      const params = new URLSearchParams({ limit: '1000000' });
      const current = new URLSearchParams(location.search);
      const search = current.get('search') || '';
      const dateRange = current.get('date_range') || '';
      const source = current.get('source') || '';
      if (search) params.set('search', search);
      if (dateRange) params.set('date_range', dateRange);
      if (source) params.set('source', source);
      const data = await get(`/leads/?${params.toString()}`);
      const allLeads: any[] = data.data;

      const headers = ['Email', 'First Name', 'Last Name', 'Company', 'Date Created'];
      const csvData = allLeads.map((lead: any) => [
        lead.email || '',
        lead.first_name || '',
        lead.last_name || '',
        lead.company || '',
        formatDate(lead.created_at) || ''
      ].map(escapeCsv));

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Aimdoc_contacts_export_${formatDate(new Date())}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      captureEvent('export_leads', {});
      toast.success('Contacts exported successfully', toastConfig);
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast.error('Failed to export contacts', toastConfig);
    } finally {
      setExporting(false);
    }
  };


  // Define columns for TanStack Table
  const renderSortHeader = (label: string, field: string) => (
    <SortHeader
      label={label}
      field={field}
      activeField={sortField}
      activeOrder={sortOrder}
      onSort={(f) => toggleField(f, LEADS_SORTS.find((s) => s.field === f)?.defaultOrder)}
    />
  );

  const columns = React.useMemo(() => [
    {
      accessorKey: 'email',
      size: 240,
      header: () => renderSortHeader('Email', 'email'),
      cell: ({ row }: any) => (
        // <Link
        //   to={`/leads/${row.original.id}`}
        //   className="text-md font-semibold hover:underline"
        //   onClick={(e) => e.stopPropagation()}
        // >
        //   {row.original.email || 'N/A'}
        // </Link>
        <span className="text-md flex items-center gap-1.5 min-w-0" onClick={() => setSelectedLead(row.original)}>
          <span className="hover:underline truncate" title={row.original.email || 'N/A'}>
            {row.original.email || 'N/A'}
          </span>
          {row.original.identity_verified_at && (
            <span title="Identity verified via signed SDK identify" className="flex-shrink-0 text-emerald-500">
              <ShieldCheck className="h-3.5 w-3.5" />
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'name',
      size: 180,
      header: () => renderSortHeader('Name', 'first_name'),
      cell: ({ row }: any) => {
        const name = [row.original.first_name, row.original.last_name].filter(Boolean).join(' ');
        return (
          <span className="text-md block truncate" title={name || 'N/A'}>
            {name || 'N/A'}
          </span>
        );
      },
    },
    {
      accessorKey: 'company',
      size: 180,
      header: () => renderSortHeader('Company', 'company'),
      cell: ({ row, getValue }: any) => (
        row.original.account_id ? (
          <Link
            to={`/accounts/${row.original.account_id}`}
            className="text-md block truncate hover:underline"
            title={getValue() || 'N/A'}
            onClick={(e) => e.stopPropagation()}
          >
            {getValue() || 'N/A'}
          </Link>
        ) : (
          <span className="text-md block truncate" title={getValue() || 'N/A'}>
            {getValue() || 'N/A'}
          </span>
        )
      ),
    },
    {
      id: 'objectives',
      size: 130,
      header: 'Objectives',
      cell: ({ row }: any) => <ObjectiveProgress progress={objectiveProgress[row.original.id]} />,
    },
    {
      accessorKey: 'revenue',
      size: 120,
      header: () => renderSortHeader('Revenue', 'revenue'),
      cell: ({ row }: any) => {
        const revenue = row.getValue('revenue');
        if (!revenue) return <span className="text-md">-</span>;
        
        // Format the revenue value
        const formattedRevenue = new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(revenue);
        
        let badgeClass = "inline-flex items-center px-2.5 py-0.5 rounded-xl text-xs font-bold ";
        let tooltipText = '';
        
        if (revenue >= 10000) {
          badgeClass += "text-green-600";
          tooltipText = 'High value contact';
        } else if (revenue >= 1000) {
          badgeClass += "text-blue-600"; 
          tooltipText = 'Medium value contact';
        } else {
          badgeClass += "text-gray-600"; 
          tooltipText = 'Low value contact';
        }
        
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={badgeClass}>
                  {formattedRevenue}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText} - From HubSpot deals</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: 'source',
      size: 120,
      header: () => renderSortHeader('Source', 'source'),
      cell: ({ getValue }: any) => {
        const source = getValue();
        if (!source) return <span className="text-md text-muted-foreground">-</span>;
        return (
          <span className="inline-flex items-center gap-1.5 text-md">
            {source === 'app' ? (
              <><Monitor className="h-3.5 w-3.5 text-blue-500" /> App</>
            ) : (
              <><Globe className="h-3.5 w-3.5 text-green-500" /> Website</>
            )}
          </span>
        );
      },
    },
    {
      id: 'crm',
      size: 90,
      header: 'CRM',
      cell: ({ row }: any) => {
        const integrations = row.original.integrations;
        const crmLinks = [
          {
            name: 'Salesforce',
            url: integrations?.salesforce_contact || integrations?.salesforce,
            icon: SalesforceIcon,
          },
          {
            name: 'HubSpot',
            url: integrations?.hubspot,
            icon: HubspotIcon,
          },
          {
            name: 'Attio',
            url: integrations?.attio,
            icon: AttioIcon,
          },
        ].filter((crm) => Boolean(crm.url));

        if (!crmLinks.length) {
          return <span className="text-md text-muted-foreground">-</span>;
        }

        return (
          <div className="flex items-center gap-2">
            {crmLinks.map((crm) => (
              <TooltipProvider key={crm.name}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={crm.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`View contact in ${crm.name}`}
                    >
                      <img
                        src={crm.icon}
                        alt=""
                        className="h-5 w-5 object-contain transition-opacity hover:opacity-75"
                      />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>View contact in {crm.name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'created_at',
      size: 150,
      header: () => renderSortHeader('Date Created', 'created_at'),
      cell: ({ getValue }: any) => (
        <span className="text-md">
          {formatDate(getValue())}
        </span>
      ),
    },
    {
      id: 'actions',
      size: 56,
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }: any) => {
        const contactName =
          [row.original.first_name, row.original.last_name].filter(Boolean).join(' ')
          || row.original.email
          || 'this contact';

        return (
          <ContactActions
            contactName={contactName}
            onDelete={() => handleDelete(row.original.id)}
          />
        );
      },
    },
  ], [handleDelete, sortField, sortOrder, toggleField, objectiveProgress]);

  // Create table instance. Sorting is server-side (driven by the URL via
  // useTableSort), so no client-side sorted row model is needed.
  const table = useReactTable({
    data: leads,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const LeadsLoadingSkeleton = () => (
    <TableBody>
      {[1, 2].map((i) => (
        <TableRow key={i} className="animate-pulse">
          {columns.map((column: any, index) => (
            <TableCell
              key={column.id || column.accessorKey}
              className={cn('px-6 py-4', getResponsiveColumnClass(column.id || column.accessorKey))}
            >
              <div className={cn('h-4 bg-muted rounded text-md', index % 2 ? 'w-1/2' : 'w-3/4')} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );


  return (
    <>
      {leads && (
        <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
          <h1 className='text-4xl font-bold flex items-center gap-2'>Contacts</h1>
          <p className='mt-4 text-sm text-slate-500'>Browse through contacts collected by your agents.</p>
          <Separator className="my-4" />
          <DataTableToolbar
            className="mt-2"
            filters={leadsFilters}
            values={filterValues}
            onChange={setFilter}
            onClear={clearFilter}
            onClearAll={clearAll}
            search={{ value: searchValue, onChange: setSearch, placeholder: 'Search for contacts..' }}
            rightSlot={
              <SortControl
                sorts={LEADS_SORTS}
                activeField={sortField}
                activeOrder={sortOrder}
                isDefault={isDefaultSort}
                onSelect={setSort}
                onClear={clearSort}
              />
            }
          />
          <div className="flex flex-col mt-4">
            <div className="-m-1.5 overflow-x-auto scrollbar-hidden">
              <div className="p-1.5 w-full align-middle">
                <div className="border rounded-lg shadow overflow-hidden">
                  <Table className="table-fixed w-full">
                    <TableHeader>
                      {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <TableHead
                              key={header.id}
                              style={{ width: header.getSize() }}
                              className={cn('truncate', getResponsiveColumnClass(header.column.id))}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    {loading ? (
                      <LeadsLoadingSkeleton />
                    ) : (
                      <TableBody>
                        {table.getRowModel().rows.length ? (
                          table.getRowModel().rows.map(row => (
                            <TableRow
                              key={row.id}
                              className="text-md cursor-pointer"
                              onClick={(event) => {
                                if ((event.target as HTMLElement).closest('[data-row-actions]')) {
                                  return;
                                }
                                setSelectedLead(row.original);
                              }}
                            >
                              {row.getVisibleCells().map(cell => (
                                <TableCell
                                  key={cell.id}
                                  style={{ width: cell.column.getSize() }}
                                  className={cn('overflow-hidden', getResponsiveColumnClass(cell.column.id))}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                              No contacts
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                  </Table>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <PageSizeSelect value={perPage} onChange={handlePerPageChange} disabled={loading} />
              {totalPages > 1 && (
                <div className='flex flex-row items-center gap-4'>
                  <Button
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1 || loading}
                    variant="outline"
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-semibold">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages || loading}
                    variant="outline"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>

            <Button
              className='mt-4 w-full'
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting}
            >
              <Download /> {exporting ? 'Exporting...' : 'Export to CSV'}
            </Button>
          </div>
        </div>
      )}
      {showModal && (
        <div className="place-content-center fixed inset-0 bg-slate-600 bg-opacity-50 backdrop-blur-sm overflow-y-auto h-screen w-screen z-40 flex items-center justify-center">
          <div className="flex h-5/6 w-2/3 items-center justify-center">
            <div className="flex h-full w-full">

              <CreateAgent
                toggleModal={toggleModal}
                modalVisible={showModal}
              />
            </div>
          </div>
        </div>
      )}
      <LeadSheet
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onLeadUpdate={(updatedLead) => setSelectedLead(updatedLead)}
      />
    </>
  );
};

export default LeadLists;
